/** User-facing bulk summaries: counts describe outcomes, and Skipped is reserved for the Visit status. */
import { BulkActionUserRoomPayload } from '../sockets/rooms/user.rooms';
import { VisitBulkActionId } from '../planned.work/visit/visit.bulk.actions';
import { VisitCreateJobSkip } from '../planned.work/visit/visit.create.jobs';
import { TaskApprovalStatus } from '../planned.work/visit/task.approval';
import { BulkActionOutcomeSummary, EntityBoardBulkActionRowResult } from './board.bulk.action.request';
import { BulkActionStatus } from './enums';

/** Completion publishers group reasons and retained auto-approval counts without sending record details. */
export function summarizeBulkActionResults(results: readonly EntityBoardBulkActionRowResult[]): BulkActionOutcomeSummary[] {

    const groups = new Map<string, BulkActionOutcomeSummary>();
    for (const result of results) {
        if (result.status === BulkActionStatus.Success && result.reasonCode !== TaskApprovalStatus.AutoApproved) continue;
        const key = `${result.status}:${result.reasonCode ?? result.message ?? ''}`;
        const existing = groups.get(key);
        if (existing) existing.count++;
        else groups.set(key, {
            status: result.status,
            count: 1,
            reasonCode: result.reasonCode,
            message: result.message,
        });
    }
    // Losing the successful auto-approved group would falsely count those moves as manual approvals.
    return [...groups.values()].sort((a, b) => {
        if (a.status === BulkActionStatus.Success && b.status !== BulkActionStatus.Success) return -1;
        if (b.status === BulkActionStatus.Success && a.status !== BulkActionStatus.Success) return 1;
        return b.count - a.count;
    }).slice(0, 6);
}

/**
 * Totals of the rows that moved Visit work, for the terminal Reschedule message; undefined when no row moved
 * any. Resulting Visits are distinct across rows, so two rows landing on one Visit count it once.
 */
export function summarizeBulkActionMovement(results: readonly EntityBoardBulkActionRowResult[]): BulkActionUserRoomPayload['movement'] {

    const moved = results.filter(result => result.status === BulkActionStatus.Success && (result.movement?.tasksMoved ?? 0) > 0);
    if (!moved.length) return undefined;

    return {
        tasksMoved: moved.reduce((total, result) => total + result.movement!.tasksMoved, 0),
        fromVisits: moved.length,
        resultingVisits: new Set(moved.flatMap(result => result.movement!.resultingVisitIds)).size,
    };
}

/** Totals of task stamps written by Approve / Withdraw rows and Reschedule rows kept approved. */
export function summarizeBulkActionApproval(results: readonly EntityBoardBulkActionRowResult[]): BulkActionUserRoomPayload['approval'] {

    const rows = results.filter(result => result.approval);
    if (!rows.length) return undefined;

    return {
        tasksChanged: rows.reduce((total, result) => total + result.approval!.tasksChanged, 0),
        tasksSkipped: rows.reduce((total, result) => total + result.approval!.tasksSkipped, 0),
        ...(rows.some(result => result.approval!.tasksAutoApproved !== undefined)
            ? { tasksAutoApproved: rows.reduce((total, result) => total + (result.approval!.tasksAutoApproved ?? 0), 0) }
            : {}),
    };
}

/**
 * The shared executor uses this for queued, processing and terminal messages, for one record or many.
 * `queuedTasks` is the task count a task-level approval run carries in `actionData.rows`; it only words the queued line.
 * `keepApproved` is a Reschedule's `actionData.keepApproved`: the run also stamps the moved tasks, so it is worded as approval.
 */
export function formatBulkActionMessage(event: Pick<BulkActionUserRoomPayload, 'status' | 'total' | 'success' | 'skipped' | 'failed' | 'outcomes' | 'movement' | 'approval'>, actionId?: string, options: { queuedTasks?: number; keepApproved?: boolean } = {}): string {

    const { queuedTasks } = options;
    const keptApproved = actionId === VisitBulkActionId.Reschedule && options.keepApproved === true;
    const visitAction = Object.values(VisitBulkActionId).some(id => id === actionId);
    const approvalAction = keptApproved || isApprovalBulkAction(actionId);
    const singular = visitAction ? 'Visit' : 'record';
    const plural = visitAction ? 'Visits' : 'records';
    const target = (count: number) => `${count} ${count === 1 ? singular : plural}`;
    if (event.status === BulkActionStatus.Pending) {
        if (!approvalAction) return `Queued for ${target(event.total)}.`;
        return queuedTasks ? `Queued for ${queuedTasks} ${queuedTasks === 1 ? 'task' : 'tasks'}.` : 'Queued.';
    }
    if (event.status === BulkActionStatus.Processing) {
        if (approvalAction) return event.total === 1 ? 'Processing…' : `${event.success + event.skipped + event.failed} of ${event.total} processed.`;
        if (event.total === 1) return `Processing ${target(1)}…`;
        return `${event.success + event.skipped + event.failed} of ${event.total} ${visitAction ? 'Visits' : 'records'} processed.`;
    }
    const wording = keptApproved ? { completed: 'rescheduled and approved', failure: 'The action' } : actionWording(actionId);
    const messages: string[] = [];
    let autoApproved = 0;
    if (keptApproved) {
        for (const outcome of event.outcomes ?? []) {
            if (outcome.status !== BulkActionStatus.Success) continue;
            if (outcome.reasonCode !== TaskApprovalStatus.AutoApproved) continue;
            autoApproved += outcome.count;
        }
        autoApproved = Math.min(event.success, autoApproved);
    }
    const changed = event.success - autoApproved;
    if (event.success > 0) {
        if (changed > 0) {
            const jobs = `${changed} ${changed === 1 ? 'job' : 'jobs'}`;
            const tasks = event.approval?.tasksChanged;
            if (actionId === VisitBulkActionId.WithdrawApproval) {
                messages.push(tasks ? `Approval removed from ${tasks} ${tasks === 1 ? 'task' : 'tasks'}.` : 'Approval removed from the selected tasks.');
            } else {
                let subject = target(changed);
                if (actionId === VisitBulkActionId.CreateJobs) subject = jobs;
                if (approvalAction) {
                    subject = 'Tasks';
                    if (tasks) subject = `${tasks} ${tasks === 1 ? 'task' : 'tasks'}`;
                }
                messages.push(`${subject} ${wording.completed}.`);
            }
        }
        if (autoApproved > 0) {
            const count = event.approval?.tasksAutoApproved;
            const rowMessage = event.outcomes?.find(outcome => outcome.status === BulkActionStatus.Success && outcome.reasonCode === TaskApprovalStatus.AutoApproved)?.message;
            // One record reads its row's own wording; a selection counts the tasks across rows.
            if (event.total === 1 && rowMessage) messages.push(sentence(rowMessage));
            else messages.push(count ? `${count} ${count === 1 ? 'task' : 'tasks'} rescheduled (auto-approved line).` : 'Tasks rescheduled (auto-approved line).');
        }
    } else if (actionId === VisitBulkActionId.CreateJobs) {
        messages.push(event.total === 1 ? 'No job created.' : 'No jobs created.');
    } else if (approvalAction) {
        const withdraw = actionId === VisitBulkActionId.WithdrawApproval;
        const otherReason = event.outcomes?.find(outcome => outcome.status === BulkActionStatus.Skipped && outcome.reasonCode !== nothingToDoReason(actionId));
        if (event.failed > 0 || (otherReason && event.total > 1)) messages.push(withdraw ? 'No approvals removed.' : 'No tasks approved.');
        // Auto-approved or no-longer-scheduled work is not "already approved": one record says its own reason.
        else if (otherReason?.message) messages.push(sentence(otherReason.message));
        else messages.push(withdraw ? 'Nothing to remove: the selected tasks are not approved.' : 'Nothing to approve: the selected tasks are already approved.');
    } else {
        messages.push('No changes made.');
    }
    if (event.movement && actionId === VisitBulkActionId.Reschedule) messages.push(movedSentence(event.movement));
    if (event.total === 1) {
        const outcome = event.outcomes?.find(item => item.status !== BulkActionStatus.Success && item.message);
        const reason = outcome?.message ? sentence(outcome.message) : undefined;
        // The "Nothing to …" sentence, or a reason already used above, is said once.
        const nothingToDo = nothingToDoReason(actionId);
        const said = (nothingToDo !== undefined && outcome?.reasonCode === nothingToDo) || (reason !== undefined && messages.includes(reason));
        if (reason && !said) messages.push(reason);
        else if (!reason && (event.failed > 0 || event.status === BulkActionStatus.Failed)) messages.push('Couldn’t complete the action. View details.');
        else if (!reason && event.skipped > 0) messages.push('View the reason for this record.');
        return messages.join(' ');
    }
    let explained = 0;
    if (actionId === VisitBulkActionId.CreateJobs) {
        for (const outcome of event.outcomes ?? []) {
            if (outcome.status !== BulkActionStatus.Skipped) continue;
            const message = jobCreationBlocker(outcome.reasonCode, outcome.count);
            if (!message) continue;
            messages.push(message);
            explained += outcome.count;
            if (messages.length >= 4) break;
        }
    }
    const unexplained = Math.max(0, event.skipped - explained);
    if (unexplained) messages.push(`${unexplained} not changed.`);
    if (event.failed) messages.push(`${wording.failure} failed for ${target(event.failed)}.`);
    if (unexplained || event.failed || event.status === BulkActionStatus.Failed) messages.push('View details for each record.');
    return messages.join(' ');
}

function actionWording(actionId?: string) {

    switch (actionId) {
        case VisitBulkActionId.CreateJobs: return { completed: 'created', failure: 'Job creation' };
        case VisitBulkActionId.Approve: return { completed: 'approved', failure: 'Approval' };
        case VisitBulkActionId.Reschedule: return { completed: 'rescheduled', failure: 'Rescheduling' };
        case VisitBulkActionId.Skip: return { completed: 'marked Skipped', failure: 'The action' };
        case VisitBulkActionId.WithdrawApproval: return { completed: 'removed from approval', failure: 'Removing approval' };
        default: return { completed: 'updated', failure: 'The action' };
    }
}

function jobCreationBlocker(reason: string | undefined, count: number): string | undefined {

    const visits = `${count} ${count === 1 ? 'Visit' : 'Visits'}`;
    const needs = count === 1 ? 'needs' : 'need';
    switch (reason) {
        case VisitCreateJobSkip.NeedsDateDecision: return `${visits} ${needs} ${count === 1 ? 'its past appointment date' : 'their past appointment dates'} confirmed or rescheduled.`;
        case VisitCreateJobSkip.NotApproved: return `${visits} ${needs} approval first.`;
        case VisitCreateJobSkip.HasJob: return `${visits} already ${count === 1 ? 'has a Job' : 'have Jobs'}.`;
        case VisitCreateJobSkip.MissingJobDetails: return `${visits} ${count === 1 ? 'is' : 'are'} missing required Job details. View details.`;
        case VisitCreateJobSkip.PlanPaused: return count === 1 ? `${visits} belongs to a paused Service Plan.` : `${visits} belong to paused Service Plans.`;
        case VisitCreateJobSkip.Skipped: return `${visits} ${count === 1 ? 'is' : 'are'} marked Skipped. Restore ${count === 1 ? 'it' : 'them'} first.`;
        case VisitCreateJobSkip.ApprovalChanged: return count === 1 ? '1 Visit has tasks changed since approval. Approve them again.' : `${visits} have tasks changed since approval. Approve them again.`;
        case VisitCreateJobSkip.Missed: return `${visits} ${needs} rescheduling before Job creation.`;
        case VisitCreateJobSkip.NeedsReview: return `${visits} ${needs} review before Job creation.`;
        case VisitCreateJobSkip.NoWork: return `${visits} ${count === 1 ? 'has' : 'have'} no tasks or items.`;
        default: return undefined;
    }
}

/** "Moved 12 tasks from 4 Visits into 3 Visits." */
function movedSentence(movement: NonNullable<BulkActionUserRoomPayload['movement']>): string {

    const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

    return `Moved ${plural(movement.tasksMoved, 'task')} from ${plural(movement.fromVisits, 'Visit')} into ${plural(movement.resultingVisits, 'Visit')}.`;
}

/** The skip reason an approval row reports when the selection was already in the asked-for state; the "Nothing to …" sentence says it. */
function nothingToDoReason(actionId?: string): string | undefined {

    if (actionId === VisitBulkActionId.Approve) return 'AlreadyApproved';
    if (actionId === VisitBulkActionId.WithdrawApproval) return 'NotApproved';
    return undefined;
}

function isApprovalBulkAction(actionId?: string): boolean {

    return actionId === VisitBulkActionId.Approve || actionId === VisitBulkActionId.WithdrawApproval;
}

function sentence(message: string): string {

    const trimmed = message.trim();
    return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}
